import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  OnInit,
  SimpleChange,
  OnDestroy,
  OnChanges,
} from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { get, includes, isEmpty, isFunction } from 'lodash';

import { Subscription } from '@shared/rx';
import { IMenuItem } from '@app/xenial-ux/interfaces';
import { ToolbarButtonDefinition, TrackByValue } from '@shared/types';
import { ERROR_PAGE_ROUTE } from '@app/routes';
import { StateService } from '@app/shared/services/state-service/state.service';
import { I18n } from '@app/i18n/strings-keys';
import { trackByValue } from '@app/shared/utils/common-utils';
import { XVII18nService } from '@app/i18n/services';

export interface IBreadCrumb {
  title: string;
  route: string;
}
@Component({
  selector: 'xux-breadcrumb-navbar',
  templateUrl: './xux-breadcrumb-navbar.component.html',
  styleUrls: ['./xux-breadcrumb-navbar.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class XUXBreadcrumbNavbarComponent
  implements OnInit, OnDestroy, OnChanges
{
  @Input() public isIframeMode: boolean;
  @Input() public selectedMenuItem: IMenuItem;
  @Input() public hiddenMenu: boolean;
  @Input() public appName: string;
  @Input() public buttons: ToolbarButtonDefinition[];

  public breadcrumbs: IBreadCrumb[] = [];
  public trackByValue: TrackByValue = trackByValue;
  private lastRoutePart: string;
  private subs: Subscription[] = [];
  private routeParts: string[] = [];

  public constructor(
    private readonly router: Router,
    private readonly changeDetectorRef: ChangeDetectorRef,
    private readonly stateService: StateService,
    private i18nService: XVII18nService,
  ) {}

  public ngOnInit(): void {
    this.subs.push(
      this.router.events.subscribe((data) => {
        if (data instanceof NavigationEnd) {
          const routeParts = this.router.url.split('/');
          // first element is '' when route begins with /
          if (routeParts.length && routeParts[0] === '') {
            routeParts.shift();
          }
          this.routeParts = routeParts;

          if (routeParts.length > 1) {
            this.lastRoutePart = routeParts[routeParts.length - 1];
          } else {
            this.lastRoutePart = null;
          }
          this.rebuild();
          this.changeDetectorRef.detectChanges();
        }
      }),
    );
  }

  public ngOnChanges(
    changes: Record<keyof XUXBreadcrumbNavbarComponent, SimpleChange>,
  ): void {
    if (changes.selectedMenuItem) this.rebuild();
  }

  public ngOnDestroy(): void {
    this.subs.forEach((sub) => sub.unsubscribe());
  }

  public buttonClick(btn: ToolbarButtonDefinition) {
    if (isFunction(btn?.click)) {
      btn.click();
    }
  }

  private rebuild(): void {
    if (includes(this.routeParts, ERROR_PAGE_ROUTE)) {
      const errorPageState = this.stateService.getByPath(['ui', 'errorPage']);

      const errorTitle = get(errorPageState, 'title', '');

      this.breadcrumbs = [
        {
          title: this.appName,
          route: '',
        },
        {
          title: !isEmpty(errorTitle)
            ? errorTitle
            : this.i18nService.instant(I18n.ErrorMessages.UnexpectedError),
          route: '',
        },
      ];

      return;
    }
    this.breadcrumbs = [];
    this.breadcrumbs.push({
      title: this.appName,
      route: '',
    });
    if (this.selectedMenuItem) {
      this.breadcrumbs.push({
        title:
          this.selectedMenuItem.customListTitle || this.selectedMenuItem.title,
        route: `/${this.selectedMenuItem.route}`,
      });
    }
    if (this.lastRoutePart) {
      const crumbTitleMap = {
        add: I18n.Common.Create,
        list: I18n.Common.List,
      };
      this.breadcrumbs.push({
        title: get(crumbTitleMap, this.lastRoutePart, I18n.Common.Edit),
        route: `/${this.lastRoutePart}`,
      });
    }
  }
}
